import DatePicker, {
  ReactDatePickerCustomDayNameProps,
  ReactDatePickerCustomHeaderProps,
} from "react-datepicker";
import React, { forwardRef } from "react";
import {
  DateCell,
  DateHeader,
  DateInputContainer,
  HeaderContainer,
  LegendContainer,
  LegendRow,
  LegendTitle,
} from "./FlashPCBDatePickerStyles";
import Icon from "../Icon";
import {
  isClosedOnDate,
  isFastestOption,
  isFasterOption,
  isToday,
  getOption,
  FASTEST_BASE_LEAD_TIME,
  FASTER_BASE_LEAD_TIME,
  isInThePast,
  isWeekend,
} from "../../../utils/hooks/commonly-stored/leadTimeOptions";
import {
  dateInputStringToDate,
  dateStringToDate,
  dateToHumanReadableDate,
  dateToLocalDateString,
  leadTimeInfoString,
  twentyFourHourToTwelveHour,
} from "../../../utils/valueFormatting";
import { COLORS } from "../../../constants/Styles";
import {
  LeadTimeOptions,
  ProductionStatus,
  Shipment,
} from "../../../reducers/uploadedBoard";
import { FlashPCBSettings } from "../../../utils/settings";
import FlashPCBTooltip from "../FlashPCBTooltip";
import InfoIcon from "../FlashPCBTooltip/InfoIcon";
import { datesMatch } from "../../../utils/dates";

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type DatePickerInputProps = {
  className?: string;

  value?: string;
  onClick?: () => void;
  leadTimeDays: number | null;
};

const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  ({ className, value, onClick, leadTimeDays }, ref) => {
    const getValue = () => {
      if (value) {
        const date = dateInputStringToDate(value);
        if (leadTimeDays !== null) {
          return leadTimeInfoString(leadTimeDays, dateToLocalDateString(date));
        }
        return dateToHumanReadableDate(date, true);
      }
    };

    return (
      <DateInputContainer onClick={onClick} className={className} ref={ref}>
        {getValue()}
        <Icon
          src="/icons/calendar.svg"
          alt="Calendar"
          iconDimension={16}
          styleProps={{ margin: "0" }}
        />
      </DateInputContainer>
    );
  },
);
DatePickerInput.displayName = "DatePickerInput";

export function DatePickerHeader({
  date,
  decreaseMonth,
  increaseMonth,
}: ReactDatePickerCustomHeaderProps) {
  return (
    <HeaderContainer>
      <Icon
        src={"/icons/retract.svg"}
        alt="Decrease month"
        iconDimension={12}
        styleProps={{
          margin: "0.5rem",
          transform: "rotate(-90deg)",
          cursor: "pointer",
        }}
        onClick={decreaseMonth}
      />
      {MONTHS[date.getMonth()]} {date.getFullYear()}
      <Icon
        src={"/icons/retract.svg"}
        alt="Increase month"
        iconDimension={12}
        styleProps={{
          margin: "0.5rem",
          transform: "rotate(90deg)",
          cursor: "pointer",
        }}
        onClick={increaseMonth}
      />
    </HeaderContainer>
  );
}

export function DayOfWeek({ shortName }: ReactDatePickerCustomDayNameProps) {
  return <DateHeader>{shortName}</DateHeader>;
}

interface DateDisplayOptions {
  backgroundColor: string;
  message: string;
}

function getDisplayOptions(
  date: Date,
  leadTimeOptions: LeadTimeOptions,
  showPricing: boolean,
): DateDisplayOptions | null {
  if (leadTimeOptions === null) {
    return null;
  }
  const closure = isClosedOnDate(date, leadTimeOptions);
  if (closure !== null) {
    return {
      message: `Closed for ${closure}`,
      backgroundColor: COLORS.warn,
    };
  } else if (showPricing && isFastestOption(date, leadTimeOptions)) {
    return {
      message: "This is our fastest pricing tier",
      backgroundColor: COLORS.fastest,
    };
  } else if (showPricing && isFasterOption(date, leadTimeOptions)) {
    return {
      message: "This is our fast pricing tier",
      backgroundColor: COLORS.faster,
    };
  }
  return null;
}

function getTooltipContent(
  date: Date,
  shipmentsOnThisDate: Shipment[],
  isLeadTimeStartDate: boolean,
  isActualLeadTimeStartDate: boolean,
  message: string | undefined,
  endOfDayHours: number,
) {
  if (shipmentsOnThisDate.length > 0) {
    return `${shipmentsOnThisDate.map((shipment) => `${shipment.num_boards.toLocaleString()} boards`).join(", ")} shipping ${dateToHumanReadableDate(date)}`;
  } else if (isLeadTimeStartDate) {
    if (isActualLeadTimeStartDate) {
      return "This project's lead time started on this date";
    } else {
      return `Potential lead time start date (the lead time starts the following business day for orders placed after ${twentyFourHourToTwelveHour(endOfDayHours)})`;
    }
  }
  return message;
}

interface LeadTimeStartDateContext {
  actual: boolean;
  date: Date;
}

function getLeadTimeStartDate(
  shipments: Shipment[],
  leadTimeOptions: LeadTimeOptions,
): LeadTimeStartDateContext {
  if (shipments[0]?.date_lead_time_started) {
    return {
      actual: true,
      date: dateStringToDate(shipments[0].date_lead_time_started),
    };
  }
  return {
    actual: false,
    date: dateStringToDate(leadTimeOptions.next_lead_time_start_date),
  };
}

export function FlashPCBCalendar({
  shipments,
  leadTimeOptions,
}: {
  shipments: Shipment[];
  leadTimeOptions: LeadTimeOptions;
}) {
  const showPricing =
    shipments[0]?.production_status == ProductionStatus.QUOTING;

  const renderDate = (day: number, date: Date) => {
    const shipmentsOnThisDate =
      shipments?.filter((shipment) => {
        const shipDate = dateStringToDate(shipment.ship_date);
        return datesMatch(shipDate, date);
      }) || [];

    const displayOptions = getDisplayOptions(
      date,
      leadTimeOptions,
      showPricing,
    );

    const leadTimeStartDate = getLeadTimeStartDate(shipments, leadTimeOptions);

    const isLeadTimeStartDate = datesMatch(leadTimeStartDate.date, date);

    return (
      <FlashPCBTooltip
        content={getTooltipContent(
          date,
          shipmentsOnThisDate,
          isLeadTimeStartDate,
          leadTimeStartDate.actual,
          displayOptions?.message,
          leadTimeOptions.local_lead_time_eod,
        )}
      >
        <DateCell
          today={isToday(date)}
          leadTimeStartDate={isLeadTimeStartDate}
          inThePast={isInThePast(date)}
          isWeekend={isWeekend(date)}
          selected={shipmentsOnThisDate.length > 0}
          backgroundColor={displayOptions?.backgroundColor}
        >
          <div>{day}</div>
        </DateCell>
      </FlashPCBTooltip>
    );
  };

  return (
    <DatePicker
      renderCustomDayName={DayOfWeek}
      renderDayContents={renderDate}
      renderCustomHeader={DatePickerHeader}
      inline
    />
  );
}

export function Legend({
  leadTimeOptions,
  flashPCBSettings,
  showShipDateInLegend,
}: {
  leadTimeOptions: LeadTimeOptions;
  flashPCBSettings: FlashPCBSettings;
  showShipDateInLegend?: boolean;
}) {
  if (leadTimeOptions === null) {
    return null;
  }
  return (
    <LegendContainer>
      <LegendTitle>Legend</LegendTitle>
      {getOption(leadTimeOptions, FASTEST_BASE_LEAD_TIME) && (
        <LegendRow>
          <DateCell backgroundColor={COLORS.fastest} />
          <div>Our fastest pricing tier</div>
        </LegendRow>
      )}
      {getOption(leadTimeOptions, FASTER_BASE_LEAD_TIME) && (
        <LegendRow>
          <DateCell backgroundColor={COLORS.faster} />
          <div>Our faster pricing tier</div>
        </LegendRow>
      )}
      <LegendRow>
        <DateCell backgroundColor={COLORS.warn} />
        <div>Office closure</div>
      </LegendRow>
      <LegendRow>
        <DateCell leadTimeStartDate={true} />
        <div>
          Lead time start date{" "}
          <FlashPCBTooltip
            content={`For orders placed after ${twentyFourHourToTwelveHour(flashPCBSettings.local_lead_time_eod)} the lead time starts the following business day.`}
          >
            <InfoIcon />
          </FlashPCBTooltip>
        </div>
      </LegendRow>
      {showShipDateInLegend && (
        <LegendRow>
          <DateCell selected={true} />
          <div>Selected ship date</div>
        </LegendRow>
      )}
    </LegendContainer>
  );
}
